import type { BrandedIconProps } from './types';

/**
 * Visa — global card network. The mark is the wordmark "VISA" in
 * `#1A1F71` (Visa blue) on a white card-shaped background, with the
 * `#F7B600` (Visa gold) baseline accent that lives on physical cards
 * since the 2014 brand refresh.
 *
 * Rationale for a card-shaped mark
 * --------------------------------
 * The bare "VISA" wordmark is illegible below ~32px. In the checkout the
 * icon renders inside 24-40px chips alongside the Mastercard mark, so we
 * render the recognizable "card" container (rounded rect + wordmark + gold
 * stripe) — same visual language Visa itself uses on its checkout button
 * assets.
 *
 * Colors per Visa Brand Guidelines (https://usa.visa.com/run-your-business/visa-brand-guidelines.html):
 *  - `#1A1F71` Visa blue (wordmark + card background tint variant).
 *  - `#F7B600` Visa gold (accent stripe).
 *  - `#FFFFFF` neutral background.
 *
 * Path for the wordmark adapted from simple-icons (CC0,
 * https://simpleicons.org/icons/visa). DO NOT recolor — see
 * `BrandedIconProps` for rationale.
 */
export function VisaIcon({
  size = 24,
  className,
  'aria-label': ariaLabel = 'Visa',
}: BrandedIconProps): JSX.Element {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      width={size}
      height={size}
      role="img"
      aria-label={ariaLabel}
      className={className}
    >
      {/* Card body */}
      <rect
        x="1"
        y="5"
        width="22"
        height="14"
        rx="2.5"
        fill="#FFFFFF"
        stroke="#E2E8F0"
        strokeWidth="0.75"
      />
      {/* Gold baseline stripe — Visa gold */}
      <rect x="1" y="16.25" width="22" height="1.25" fill="#F7B600" />
      {/* "VISA" wordmark — Visa blue, optimized for legibility at 24px */}
      <path
        fill="#1A1F71"
        d="M9.42 9.45 8.13 14.6h-1.55l1.29-5.15h1.55Zm6.5 3.32.82-2.26.47 2.26h-1.29Zm1.74 1.83h1.43L17.84 9.45h-1.32c-.3 0-.55.17-.66.44l-1.86 4.71h1.63l.32-.9h1.99l.19.9Zm-4.05-1.68c.01-1.58-2.18-1.67-2.17-2.37 0-.21.21-.44.66-.5.22-.03.83-.05 1.51.27l.27-1.27a4.13 4.13 0 0 0-1.43-.26c-1.51 0-2.58.8-2.59 1.95-.01.85.76 1.32 1.34 1.6.6.29.8.48.79.74 0 .4-.48.58-.92.59-.78.01-1.23-.21-1.59-.38l-.28 1.32c.36.17 1.03.31 1.72.32 1.61 0 2.66-.79 2.67-2.01Zm-6.43-3.47-2.49 5.15h-1.65L1.81 9.97c-.07-.3-.13-.41-.36-.54a6.42 6.42 0 0 0-1.45-.49l.04-.18h2.65c.34 0 .64.22.72.6l.65 3.46 1.6-4.06h1.62Z"
      />
    </svg>
  );
}
